import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, ChevronDown, Calendar as CalendarIcon, Search, X, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PrimaryButton from '../ui/PrimaryButton';
import { StaffMember } from './types';
import { useTranslation } from 'react-i18next';
import OccupancyCalendar from './OccupancyCalendar';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface CalendarHeaderProps {
    view: 'day' | 'week';
    setView: (view: 'day' | 'week') => void;
    currentDateLabel: string;
    onPrev: () => void;
    onNext: () => void;
    onNewAppointment: () => void;
    staffMembers: StaffMember[];
    selectedStaff: StaffMember | null;
    onSelectStaff: (staff: StaffMember) => void;
    allStaffOption?: StaffMember;
    currentDate: Date;
    onSelectDate: (date: Date) => void;
    // Search props
    bookings: any[];
    clients: any[];
    getPerformanceById: (id: string) => any;
    onSelectBookingResult: (booking: any) => void;
    onToday?: () => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
    view, setView, currentDateLabel, onPrev, onNext, onNewAppointment,
    staffMembers, selectedStaff, onSelectStaff,
    allStaffOption, currentDate, onSelectDate,
    bookings, clients, getPerformanceById, onSelectBookingResult, onToday
}) => {
    const { t } = useTranslation();
    const [isStaffSelectorOpen, setIsStaffSelectorOpen] = useState(false);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Su mobile i menù sono centrati con position fixed: il top si misura dal bordo
    // inferiore del bottone che li apre, così non lo coprono mai.
    const staffSelectorRef = useRef<HTMLDivElement>(null);
    const [staffMenuTop, setStaffMenuTop] = useState(176);
    const [searchMenuTop, setSearchMenuTop] = useState(176);

    // Close search dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setIsSearchFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filtered and sorted search results
    const searchResults = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (q.length < 2) return [];

        return bookings
            .filter((b: any) => {
                if (b.status !== 'confirmed' && b.status !== 'no_show') return false;
                const client = clients.find((c: any) => c.id === b.client_id);
                if (!client) return false;
                return (client.full_name || '').toLowerCase().includes(q);
            })
            .sort((a: any, b: any) => {
                // Sort most recent first: date DESC, time DESC
                const dateCompare = b.date.localeCompare(a.date);
                if (dateCompare !== 0) return dateCompare;
                return (b.time || '').localeCompare(a.time || '');
            });
    }, [searchQuery, bookings, clients]);

    const showDropdown = isSearchFocused && searchQuery.trim().length >= 2;

    const handleResultClick = (booking: any) => {
        onSelectBookingResult(booking);
        setSearchQuery('');
        setIsSearchFocused(false);
    };

    return (
        <div className="relative px-2.5 py-1.5 md:px-6 md:py-3 border-b border-gray-200 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-1.5 xl:gap-4 bg-white z-50">

            {/* Riga 1: le frecce stanno accanto alla data, calendario e "Oggi" in coda */}
            <div className="flex items-center gap-1 w-full xl:w-auto">
                <button
                    onClick={onPrev}
                    aria-label={t('common.previous', 'Precedente')}
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-50 hover:text-clip-600 transition-colors"
                >
                    <ChevronLeft size={20} />
                </button>

                <h2 className="flex-1 xl:flex-none min-w-0 text-center xl:text-left text-[15px] md:text-xl font-bold text-gray-900 capitalize truncate">
                    {currentDateLabel}
                </h2>

                <button
                    onClick={onNext}
                    aria-label={t('common.next', 'Successivo')}
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-50 hover:text-clip-600 transition-colors"
                >
                    <ChevronRight size={20} />
                </button>

                {/* `relative` sta sul gruppo, non sul singolo bottone: così il pannello
                    del calendario si allinea al bordo destro di "Oggi" e non sfora a sinistra. */}
                <div className="relative flex items-center gap-1.5 flex-shrink-0 xl:ml-3">
                    <button
                        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                        aria-label={t('calendar.pick_date', 'Scegli data')}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${isCalendarOpen ? 'bg-clip-50 border-clip-200 text-clip-600' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-clip-600'}`}
                    >
                        <CalendarIcon size={17} />
                    </button>

                    {onToday && (
                        <button
                            onClick={onToday}
                            className="flex-shrink-0 h-9 px-3 flex items-center text-[11px] uppercase tracking-wider font-bold rounded-lg transition-colors text-clip-700 bg-clip-50 hover:bg-clip-100 border border-clip-200 active:scale-95"
                        >
                            {t('calendar.today', 'Oggi')}
                        </button>
                    )}

                    <AnimatePresence>
                        {isCalendarOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setIsCalendarOpen(false)}></div>
                                {/* Su mobile il pannello è centrato sullo schermo (fixed), così non
                                    esce mai dai bordi; da md in su torna ancorato al bottone. */}
                                <div className="fixed inset-x-0 top-36 z-50 flex justify-center md:contents">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        className="md:absolute md:top-full md:right-0 xl:left-0 xl:right-auto md:mt-2 z-50"
                                    >
                                        <OccupancyCalendar
                                            currentDate={currentDate}
                                            onSelectDate={onSelectDate}
                                            onClose={() => setIsCalendarOpen(false)}
                                        />
                                    </motion.div>
                                </div>
                            </>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Riga 2: gli strumenti, su una riga sola finché ci stanno */}
            <div className="flex items-center gap-1.5 md:gap-2 flex-wrap w-full xl:w-auto justify-center xl:justify-end xl:flex-nowrap">
                {/* Vista settimanale solo da tablet in su: su mobile l'agenda è sempre giornaliera */}
                <div className="hidden md:flex bg-gray-100 rounded-lg p-1 flex-shrink-0">
                    <button
                        onClick={() => setView('day')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors shadow-none ${view === 'day' ? 'bg-white text-gray-900 border border-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('calendar.day')}
                    </button>
                    <button
                        onClick={() => setView('week')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors shadow-none ${view === 'week' ? 'bg-white text-gray-900 border border-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {t('calendar.week')}
                    </button>
                </div>

                {/* Staff & Main Actions */}
                <div className="flex items-center gap-2 justify-center">
                    {/* Staff Selector */}
                    <div className="relative flex-shrink-0" ref={staffSelectorRef}>
                        <button
                            onClick={() => {
                                const r = staffSelectorRef.current?.getBoundingClientRect();
                                if (r) setStaffMenuTop(Math.round(r.bottom + 8));
                                setIsStaffSelectorOpen(!isStaffSelectorOpen);
                            }}
                            className={`flex items-center gap-2 pl-1 pr-3 py-1 bg-white border border-gray-200 rounded-full transition-colors group shadow-none hover:border-gray-300`}
                        >
                            {selectedStaff && (
                                <>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${selectedStaff.color} overflow-hidden`}>
                                        {selectedStaff.id === 'all' ? (
                                            <div className="w-2 h-2 bg-white rounded-full"></div>
                                        ) : selectedStaff.image ? (
                                            <img src={selectedStaff.image} alt={selectedStaff.name} className="w-full h-full object-cover" />
                                        ) : (
                                            selectedStaff.avatar
                                        )}
                                    </div>
                                    {/* Su mobile solo avatar e chevron: il nome occupa troppo spazio */}
                                    <span className="hidden md:block text-sm font-bold text-gray-700 group-hover:text-gray-900 truncate max-w-[80px]">
                                        {selectedStaff.name}
                                    </span>
                                </>
                            )}
                            <ChevronDown size={14} className="text-gray-400" />
                        </button>

                        <AnimatePresence>
                            {isStaffSelectorOpen && (
                                <>
                                    <div className="fixed inset-0 z-30" onClick={() => setIsStaffSelectorOpen(false)}></div>
                                    {/* Centrato sullo schermo su mobile, subito sotto il bottone; ancorato al bottone da md in su */}
                                    <div className="fixed inset-x-0 z-40 flex justify-center px-4 md:contents" style={{ top: staffMenuTop }}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        className="w-full max-w-xs md:w-56 md:max-w-none md:absolute md:top-full md:left-0 md:mt-2 bg-white rounded-xl shadow-xl border border-gray-100 z-40 overflow-hidden"
                                    >
                                        {/* All Staff Option */}
                                        {allStaffOption && (
                                            <button
                                                onClick={() => { onSelectStaff(allStaffOption); setIsStaffSelectorOpen(false); }}
                                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50"
                                            >
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${allStaffOption.color}`}>
                                                    <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{allStaffOption.name}</p>
                                                    <p className="text-[10px] text-gray-500">Vista d'insieme</p>
                                                </div>
                                            </button>
                                        )}

                                        {staffMembers.map((staff) => (
                                            <button
                                                key={staff.id}
                                                onClick={() => { onSelectStaff(staff); setIsStaffSelectorOpen(false); }}
                                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                                            >
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${staff.color} overflow-hidden`}>
                                                    {staff.image ? (
                                                        <img src={staff.image} alt={staff.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        staff.avatar
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{staff.name}</p>
                                                    <p className="text-[10px] text-gray-500">{staff.role}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </motion.div>
                                    </div>
                                </>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Search Bar */}
                    <div ref={searchRef} className="relative flex-shrink-0">
                        <div className={`flex items-center gap-2 bg-white border rounded-xl px-3 py-2 transition-all shadow-none ${isSearchFocused ? 'border-clip-400 ring-2 ring-clip-100' : 'border-gray-200 hover:border-gray-300'}`}>
                            <Search size={15} className={`flex-shrink-0 transition-colors ${isSearchFocused ? 'text-clip-500' : 'text-gray-400'}`} />
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => {
                                    const r = searchRef.current?.getBoundingClientRect();
                                    if (r) setSearchMenuTop(Math.round(r.bottom + 8));
                                    setIsSearchFocused(true);
                                }}
                                placeholder="Cerca cliente..."
                                className="bg-transparent text-base sm:text-sm text-gray-700 placeholder-gray-400 outline-none w-24 sm:w-32 min-w-0"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => { setSearchQuery(''); inputRef.current?.focus(); }}
                                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                >
                                    <X size={13} />
                                </button>
                            )}
                        </div>

                        {/* Results Dropdown */}
                        <AnimatePresence>
                            {showDropdown && (
                                <div className="fixed inset-x-0 z-50 flex justify-center px-4 md:contents" style={{ top: searchMenuTop }}>
                                <motion.div
                                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                                    transition={{ duration: 0.15 }}
                                    className="w-full max-w-sm md:w-72 md:max-w-none md:absolute md:top-full md:right-0 md:mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden"
                                >
                                    {searchResults.length === 0 ? (
                                        <div className="px-4 py-6 text-center">
                                            <p className="text-sm font-bold text-gray-400">Nessun risultato</p>
                                            <p className="text-xs text-gray-300 mt-0.5">Prova con un altro nome</p>
                                        </div>
                                    ) : (
                                        <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                                            {searchResults.map((booking: any) => {
                                                const client = clients.find((c: any) => c.id === booking.client_id);
                                                const performance = getPerformanceById(booking.performance_id);
                                                const bookingDate = booking.date
                                                    ? format(new Date(booking.date), 'EEE d MMM', { locale: it })
                                                    : '';
                                                const bookingTime = booking.time ? booking.time.slice(0, 5) : '';

                                                return (
                                                    <button
                                                        key={booking.id}
                                                        onClick={() => handleResultClick(booking)}
                                                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left group"
                                                    >
                                                        {/* Info */}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-bold text-gray-900 truncate group-hover:text-clip-700 transition-colors">
                                                                {client?.full_name ?? 'Cliente sconosciuto'}
                                                            </p>
                                                            <p className="text-xs text-gray-500 truncate">
                                                                {performance?.name ?? 'Servizio'}
                                                            </p>
                                                        </div>
                                                        {/* Date & Time */}
                                                        <div className="text-right flex-shrink-0">
                                                            <p className="text-xs font-bold text-gray-700 capitalize">{bookingDate}</p>
                                                            <p className="text-xs text-clip-500 font-bold mt-0.5">{bookingTime}</p>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </motion.div>
                                </div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* New Appointment Button: su mobile non c'è, l'appuntamento si crea
                        toccando direttamente uno slot libero in agenda */}
                    <div className="hidden md:block flex-shrink-0">
                        <PrimaryButton
                            label={t('calendar.new_appointment')}
                            icon={Plus}
                            onClick={onNewAppointment}
                            className="whitespace-nowrap"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarHeader;